import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  LinearProgress,
  Container,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { auth } from '../config/firebase';
import { confirmPasswordReset } from 'firebase/auth';

interface PasswordStrength {
  score: number;
  feedback: string[];
  color: 'error' | 'warning' | 'info' | 'success';
}

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Extract token and email from URL params
  const oobCode = searchParams.get('oobCode');
  const email = searchParams.get('email') || '';

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });

  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
    score: 0,
    feedback: [],
    color: 'error',
  });

  const checkPasswordStrength = (password: string): PasswordStrength => {
    const feedback: string[] = [];
    let score = 0;

    if (password.length >= 8) {
      score++;
    } else {
      feedback.push('At least 8 characters');
    }

    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) {
      score++;
    } else {
      feedback.push('Mix of uppercase and lowercase');
    }

    if (/\d/.test(password)) {
      score++;
    } else {
      feedback.push('At least one number');
    }

    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      score++;
    } else {
      feedback.push('At least one special character');
    }

    let color: 'error' | 'warning' | 'info' | 'success' = 'error';
    if (score === 4) color = 'success';
    else if (score === 3) color = 'info';
    else if (score === 2) color = 'warning';

    return { score, feedback, color };
  };

  useEffect(() => {
    if (!oobCode) {
      setError('Invalid or missing reset link. Please request a new password reset.');
    }

    setPasswordStrength(checkPasswordStrength(formData.password));
  }, [formData.password, oobCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!oobCode) {
      setError('Invalid reset link');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (passwordStrength.score < 3) {
      setError('Please create a stronger password');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Confirm the password reset with the new password
      await confirmPasswordReset(auth, oobCode, formData.password);

      setSuccess(true);
      
      // Redirect to login after a brief delay
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 3000);

    } catch (error: any) {
      console.error('Password reset error:', error);
      if (error.code === 'auth/expired-action-code') {
        setError('This password reset link has expired. Please request a new one.');
      } else if (error.code === 'auth/invalid-action-code') {
        setError('This password reset link is invalid. Please request a new one.');
      } else if (error.code === 'auth/weak-password') {
        setError('Password is too weak. Please choose a stronger password.');
      } else {
        setError('Failed to reset your password. Please try again or contact support.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof typeof formData) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData({ ...formData, [field]: e.target.value });
      setError(null);
    };

  if (success) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Card sx={{ width: '100%', boxShadow: 3 }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ textAlign: 'center' }}>
                <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  Password Reset Successful!
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  Your password has been successfully reset. You can now sign in with your new password.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Redirecting to login page...
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          component="img"
          src="/StableTrack.png"
          alt="StableTrack"
          sx={{
            height: 120,
            width: 120,
            mb: 3,
          }}
        />

        <Card sx={{ width: '100%', boxShadow: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" component="h1" gutterBottom align="center">
              Reset Your Password
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
              {email ? `Resetting password for ${email}` : 'Enter your new password below'}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                required
                label="New Password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleInputChange('password')}
                margin="normal"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {formData.password && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Password strength:
                    </Typography>
                    <Box sx={{ flex: 1, ml: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={(passwordStrength.score / 4) * 100}
                        color={passwordStrength.color}
                        sx={{ height: 8, borderRadius: 1 }}
                      />
                    </Box>
                  </Box>
                  {passwordStrength.feedback.length > 0 && (
                    <Box sx={{ ml: 2 }}>
                      {passwordStrength.feedback.map((item, index) => (
                        <Typography
                          key={index}
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}
                        >
                          <Cancel sx={{ fontSize: 14, mr: 0.5, color: 'error.main' }} />
                          {item}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  {passwordStrength.score === 4 && (
                    <Typography
                      variant="caption"
                      sx={{ display: 'flex', alignItems: 'center', mt: 0.5, ml: 2, color: 'success.main' }}
                    >
                      <CheckCircle sx={{ fontSize: 14, mr: 0.5 }} />
                      Strong password!
                    </Typography>
                  )}
                </Box>
              )}

              <TextField
                fullWidth
                required
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleInputChange('confirmPassword')}
                margin="normal"
                error={
                  formData.confirmPassword !== '' &&
                  formData.password !== formData.confirmPassword
                }
                helperText={
                  formData.confirmPassword !== '' &&
                  formData.password !== formData.confirmPassword
                    ? 'Passwords do not match'
                    : ''
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || !oobCode}
                sx={{
                  mt: 3,
                  mb: 2,
                  bgcolor: '#C9772B',
                  '&:hover': {
                    bgcolor: '#6B3410',
                  },
                }}
              >
                {loading ? 'Resetting Password...' : 'Reset Password'}
              </Button>

              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Button
                  variant="text"
                  onClick={() => navigate('/login')}
                  sx={{
                    color: '#C9772B',
                    textTransform: 'none',
                  }}
                >
                  Back to Login
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default ResetPassword;

